import { Button, Card, CardContent, FormField, Input, PageHeader, SwitchField } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useOwnerState, useUpdateSocialRules } from "../../features/backoffice/queries.ts";

export const Route = createFileRoute("/_app/social-rules")({ component: SocialRulesScreen });

function SocialRulesScreen() {
  const { data } = useOwnerState();
  const update = useUpdateSocialRules();
  const rules = data?.socialRules;
  const [giftCap, setGiftCap] = useState(3);
  const [contactLimit, setContactLimit] = useState(1);
  useEffect(() => {
    if (!rules) return;
    setGiftCap(rules.giftCapPerSession);
    setContactLimit(rules.contactLimitPerSession);
  }, [rules]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Social mode rules"
        subtitle="Owner rules restrict only. Default remains private; users cannot be forced visible."
      />
      {rules ? (
        <Card>
          <CardContent className="grid gap-3 p-5 md:grid-cols-2">
            {(["around", "gifting", "contactRequests", "games"] as const).map((key) => (
              <SwitchField
                key={key}
                label={key}
                description="Affects new social interactions immediately; user privacy still wins."
                checked={rules[key]}
                onChange={(checked) => update.mutate({ [key]: checked })}
              />
            ))}
            <p className="md:col-span-2 text-sm">
              Gift cap: {rules.giftCapPerSession}
              {"; "}
              Contact limit: {rules.contactLimitPerSession}
              {"; "}
              Default system mode: {rules.defaultMode}
            </p>
            <div className="md:col-span-2 rounded-lg border border-border p-4 space-y-2">
              <SwitchField
                label="Allow alcohol gifting / อนุญาตให้ส่งของขวัญแอลกอฮอล์"
                description="When off, alcoholic menu items cannot be sent as cross-table gifts (still orderable at the sender's own table). Also pauses automatically during last call."
                checked={rules.allowAlcoholGifting}
                onChange={(checked) => update.mutate({ allowAlcoholGifting: checked })}
              />
              <p className="text-xs text-muted-foreground">
                Legal note: this venue is responsible for verifying age and sober service at
                fulfilment (FR-GIFT-015) even when alcohol gifting is enabled. Default is off for
                every new venue (FR-OWN-117 / OQ-008). ผู้ประกอบการต้องตรวจสอบอายุและความรับผิดชอบ
                ในการให้บริการเครื่องดื่มแอลกอฮอล์เสมอ แม้เปิดใช้งานฟีเจอร์นี้แล้ว
              </p>
            </div>
            <div className="grid gap-3 rounded-lg border border-border p-4 md:col-span-2 md:grid-cols-[1fr_1fr_auto]">
              <FormField
                id="giftCapPerSession"
                label="Gift cap per session"
                hint="Restrictive cap only; it cannot force users to accept gifts."
              >
                <Input
                  id="giftCapPerSession"
                  type="number"
                  min={0}
                  value={giftCap}
                  onChange={(event) => setGiftCap(Number(event.target.value))}
                />
              </FormField>
              <FormField
                id="contactLimitPerSession"
                label="Contact requests per session"
                hint="Default private remains fixed even when caps change."
              >
                <Input
                  id="contactLimitPerSession"
                  type="number"
                  min={0}
                  value={contactLimit}
                  onChange={(event) => setContactLimit(Number(event.target.value))}
                />
              </FormField>
              <div className="flex items-end">
                <Button
                  className="w-full"
                  onClick={() =>
                    update.mutate({
                      giftCapPerSession: clampNonNegative(giftCap),
                      contactLimitPerSession: clampNonNegative(contactLimit),
                    })
                  }
                >
                  Save caps
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function clampNonNegative(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}
